export type WebVitalName = "CLS" | "LCP" | "FID" | "INP";

export type WebVitalMetric = {
  name: WebVitalName;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
  navigationType: string;
};

type MetricCallback = (metric: WebVitalMetric) => void;

type MetricState = {
  value: number;
  entries: PerformanceEntry[];
  interactionTarget?: string;
};

// LayoutShift is not in standard TypeScript lib types
interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  value: number;
}

// PerformanceObserverInit with durationThreshold for event type
interface EventObserverInit extends PerformanceObserverInit {
  durationThreshold?: number;
}

const supportsPerformanceObserver = typeof window !== "undefined" && "PerformanceObserver" in window;

const metricIds = new Map<WebVitalName, string>();

const createMetricId = (name: WebVitalName) => {
  const existingId = metricIds.get(name);
  if (existingId) {
    return existingId;
  }

  const nextId = `${name.toLowerCase()}-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  metricIds.set(name, nextId);
  return nextId;
};

const getNavigationType = () => {
  if (typeof performance === "undefined") {
    return "unknown";
  }

  const [navigationEntry] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  return navigationEntry?.type ?? "navigate";
};

const getRating = (name: WebVitalName, value: number): WebVitalMetric["rating"] => {
  switch (name) {
    case "CLS":
      if (value <= 0.1) {
        return "good";
      }
      return value <= 0.25 ? "needs-improvement" : "poor";
    case "LCP":
      if (value <= 2500) {
        return "good";
      }
      return value <= 4000 ? "needs-improvement" : "poor";
    case "FID":
    case "INP":
      if (value <= 100) {
        return "good";
      }
      return value <= 300 ? "needs-improvement" : "poor";
  }
};

const emitMetric = (name: WebVitalName, value: number, delta: number, callback: MetricCallback) => {
  callback({
    name,
    value,
    delta,
    id: createMetricId(name),
    rating: getRating(name, value),
    navigationType: getNavigationType(),
  });
};

const onHidden = (callback: () => void) => {
  const handler = () => {
    if (document.visibilityState === "hidden") {
      callback();
    }
  };

  window.addEventListener("visibilitychange", handler, true);
  window.addEventListener("pagehide", handler, true);

  return () => {
    window.removeEventListener("visibilitychange", handler, true);
    window.removeEventListener("pagehide", handler, true);
  };
};

const observeLcp = (callback: MetricCallback) => {
  if (!supportsPerformanceObserver) {
    return () => undefined;
  }

  let metricValue = 0;

  const observer = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const lastEntry = entries[entries.length - 1] as LargestContentfulPaint | undefined;
    if (!lastEntry) {
      return;
    }

    metricValue = lastEntry.startTime;
  });

  observer.observe({ type: "largest-contentful-paint", buffered: true });

  const stopHiddenListener = onHidden(() => {
    if (metricValue > 0) {
      emitMetric("LCP", metricValue, metricValue, callback);
    }
    observer.disconnect();
  });

  return () => {
    stopHiddenListener();
    observer.disconnect();
  };
};

const observeCls = (callback: MetricCallback) => {
  if (!supportsPerformanceObserver) {
    return () => undefined;
  }

  const metric: MetricState = { value: 0, entries: [] };
  let sessionValue = 0;
  let sessionEntries: LayoutShiftEntry[] = [];

  const observer = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries() as LayoutShiftEntry[]) {
      if (entry.hadRecentInput) {
        continue;
      }

      const firstSessionEntry = sessionEntries[0];
      const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

      if (
        sessionValue &&
        firstSessionEntry &&
        lastSessionEntry &&
        entry.startTime - lastSessionEntry.startTime < 1000 &&
        entry.startTime - firstSessionEntry.startTime < 5000
      ) {
        sessionValue += entry.value;
        sessionEntries.push(entry);
      } else {
        sessionValue = entry.value;
        sessionEntries = [entry];
      }

      if (sessionValue > metric.value) {
        metric.value = sessionValue;
        metric.entries = sessionEntries;
      }
    }
  });

  observer.observe({ type: "layout-shift", buffered: true });

  const stopHiddenListener = onHidden(() => {
    emitMetric("CLS", metric.value, metric.value, callback);
    observer.disconnect();
  });

  return () => {
    stopHiddenListener();
    observer.disconnect();
  };
};

const observeInp = (callback: MetricCallback) => {
  if (!supportsPerformanceObserver) {
    return () => undefined;
  }

  const metric: MetricState = { value: 0, entries: [] };

  const observer = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries() as PerformanceEventTiming[]) {
      const candidateValue = entry.duration;
      if (candidateValue >= metric.value) {
        metric.value = candidateValue;
        metric.entries = [entry];
        metric.interactionTarget = entry.name;
      }
    }
  });

  try {
    observer.observe({ type: "event", durationThreshold: 40, buffered: true } as EventObserverInit);
  } catch {
    return () => undefined;
  }

  const stopHiddenListener = onHidden(() => {
    if (metric.value > 0) {
      emitMetric("INP", metric.value, metric.value, callback);
    }
    observer.disconnect();
  });

  return () => {
    stopHiddenListener();
    observer.disconnect();
  };
};

const observeFid = (callback: MetricCallback) => {
  if (!supportsPerformanceObserver) {
    return () => undefined;
  }

  const observer = new PerformanceObserver((entryList) => {
    const firstEntry = entryList.getEntries()[0] as PerformanceEventTiming | undefined;
    if (!firstEntry) {
      return;
    }

    const value = firstEntry.processingStart - firstEntry.startTime;
    emitMetric("FID", value, value, callback);
    observer.disconnect();
  });

  try {
    observer.observe({ type: "first-input", buffered: true });
  } catch {
    return () => undefined;
  }

  return () => observer.disconnect();
};

export const observeWebVitals = (callback: MetricCallback) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const cleanups = [observeLcp(callback), observeCls(callback)];

  if (PerformanceObserver.supportedEntryTypes?.includes("event")) {
    cleanups.push(observeInp(callback));
  } else {
    cleanups.push(observeFid(callback));
  }

  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
};

export const formatWebVitalMetric = (metric: WebVitalMetric) => {
  if (metric.name === "CLS") {
    const roundedValue = Number(metric.value.toFixed(3));
    return {
      displayValue: roundedValue.toFixed(3),
      value: roundedValue,
      unit: "score",
      threshold: "< 0.1",
    };
  }

  const roundedValue = Math.round(metric.value);
  return {
    displayValue: `${roundedValue} ms`,
    value: roundedValue,
    unit: "ms",
    threshold: metric.name === "LCP" ? "< 2500 ms" : "< 100 ms",
  };
};
